/**
 * The restore lane, and the recovery notice above it.
 *
 * Two things live here because they are two halves of the same moment. The lane
 * is permanent: the import screen always carries a quiet second option for
 * "restoring, not starting", because eviction detection is best-effort and
 * recovery has to work when detection fails. The notice appears only when the
 * canary caught a wipe, and it leads with what is safe — the saves in the
 * player's backup — rather than with the loss.
 *
 * Restoring accepts any file that carries our manifest, whatever it is called.
 *
 * TODO: the lane markup belongs in index.html beside the other source cards
 * once that file is free.
 */

import { evictionVerdict } from '../durability';
import { restoreVaultBackup } from '../vault-backup';
import { toast } from './dom';

let lane: HTMLElement | null = null;
let notice: HTMLElement | null = null;

async function accept(file: File, onRestored: () => void): Promise<void> {
  try {
    const summary = await restoreVaultBackup(file);
    onRestored();
    toast(
      `Restored ${summary.restored} save${summary.restored === 1 ? '' : 's'} to your archive. `
        + 'Open the save archive to put them in a slot.',
      'ok',
      9000
    );
  } catch (error) {
    toast(`Restore failed: ${(error as Error).message}`, 'bad', 9000);
  }
}

function buildLane(onRestored: () => void): HTMLElement {
  const el = document.createElement('div');
  el.id = 'rr-restore-lane';
  // Deliberately not `.source.dropzone`. Borrowing the import cards' classes
  // also borrowed `.preview-intake .source { min-height: 286px }` and
  // `.source p { flex: 1 }`, which stretched this lane into a card-shaped
  // column with a tall empty band and pushed its button out of the panel. The
  // lane is a strip, not a card; it carries its own styles in styles.css.
  el.className = 'rr-lane';

  const text = document.createElement('div');
  text.className = 'rr-lane-text';
  const heading = document.createElement('h3');
  heading.textContent = 'Restoring from a backup?';
  const copy = document.createElement('p');
  copy.textContent =
    'Drop your rerun-vault file here. Restored saves go to your archive — nothing already in a slot is touched.';
  text.append(heading, copy);

  const input = document.createElement('input');
  input.type = 'file';
  // Not a filter that decides anything: the manifest inside is what identifies
  // a backup. This only spares the player a directory of greyed-out files.
  input.accept = '.rrdata,.zip';
  input.hidden = true;

  const button = document.createElement('button');
  button.type = 'button';
  // No `ghost` class: that is the shell's own secondary button, and this one
  // has to match the modal's instead. Styled by id in styles.css. The label is
  // written in sentence case and uppercased by CSS, exactly as the modal's
  // other buttons are, so the accessible name stays a normal sentence.
  button.textContent = 'Choose backup file…';
  button.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    input.value = '';
    if (file) void accept(file, onRestored);
  });

  for (const type of ['dragenter', 'dragover'] as const) {
    el.addEventListener(type, (event) => {
      event.preventDefault();
      el.classList.add('hot');
    });
  }
  for (const type of ['dragleave', 'dragend'] as const) el.addEventListener(type, () => el.classList.remove('hot'));
  el.addEventListener('drop', (event) => {
    event.preventDefault();
    el.classList.remove('hot');
    const file = (event as DragEvent).dataTransfer?.files?.[0];
    if (file) void accept(file, onRestored);
  });

  el.append(text, button, input);
  return el;
}

/**
 * Mounts (or re-mounts) the lane into whatever import surface exists now.
 *
 * The intake dialog is cloned out of a template by preview-landing.ts, so this
 * is called both at init and whenever the dialog opens; it is idempotent.
 */
export function mountRestoreLane(onRestored: () => void): void {
  const panel = document.querySelector('.preview-intake-panel') ?? document.querySelector('#screen-landing .wrap');
  if (!panel) return;
  if (!lane) lane = buildLane(onRestored);
  if (lane.parentElement !== panel) {
    const buy = panel.querySelector('.preview-intake-buy');
    if (buy) buy.before(lane);
    else panel.appendChild(lane);
  }
}

/**
 * The recovery notice. Shown only where the canary is certain, and worded so
 * that a player who has a backup reads one sentence and knows what to do.
 */
export function mountRecoveryNotice(onOpenImport: () => void): void {
  const verdict = evictionVerdict();
  if (verdict.state !== 'evicted' || notice) return;

  notice = document.createElement('div');
  notice.id = 'rr-recovery';

  const heading = document.createElement('strong');
  heading.textContent = 'Your browser cleared this site’s storage.';
  const copy = document.createElement('p');
  copy.textContent = verdict.lostSaves
    ? 'Saves you backed up are safe in your backup file — drop it in and they come straight back to your archive. '
      + 'Your copy of Fallout 2 is safe too: it is still on your machine, and re-importing it takes one drag.'
    : 'Your copy of Fallout 2 is still on your machine — re-importing it takes one drag. Saves are untouched.';

  const button = document.createElement('button');
  button.className = 'tiny primary';
  button.textContent = verdict.lostSaves ? 'Restore or re-import…' : 'Re-import…';
  button.addEventListener('click', onOpenImport);

  const dismiss = document.createElement('button');
  dismiss.className = 'tiny ghost';
  dismiss.textContent = 'Dismiss';
  dismiss.addEventListener('click', () => notice?.remove());

  const text = document.createElement('div');
  text.append(heading, copy);
  notice.append(text, button, dismiss);
  document.getElementById('app')?.prepend(notice);
}
